/**
 * Motion primitives — the one place to reach for Framer Motion.
 *
 * All UI animations should come from here (or from `motion.*` elements with
 * `EASE`), so motion stays consistent, respects `prefers-reduced-motion` and
 * is easy to discover. Drop-in usage:
 *
 *   <MotionFadeUp delay={0.1}>cards</MotionFadeUp>
 *
 *   <MotionStagger>
 *     <MotionStaggerItem>one</MotionStaggerItem>
 *     <MotionStaggerItem>two</MotionStaggerItem>
 *   </MotionStagger>
 *
 * Every component accepts anything a plain <motion.div> accepts (className,
 * style, role, onClick, ...) plus `delay`/`duration` — if you need more than
 * that, use <motion.div> directly and import `EASE` from here.
 */
import { motion, MotionConfig, type HTMLMotionProps, type Variants } from 'framer-motion';
import type { ReactNode } from 'react';

/** Standard easing — a soft "fast out, long settle" curve. */
export const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

/** Default durations, in seconds. */
export const DURATION = {
  /** Micro-interactions: taps, hovers, toggles. */
  fast: 0.15,
  /** Enter/exit of small surfaces: alerts, toasts, tooltips. */
  base: 0.25,
  /** Large surfaces: modals, sidebars, empty states. */
  slow: 0.35,
} as const;

interface MotionBoxProps
  extends Omit<HTMLMotionProps<'div'>, 'initial' | 'animate' | 'exit' | 'transition' | 'variants'> {
  children: ReactNode;
  delay?: number;
  duration?: number;
}

/** Soft fade-in; the baseline for anything that appears. */
export function MotionFade({ delay = 0, duration = DURATION.base, children, ...props }: MotionBoxProps) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration, delay, ease: EASE }}
      {...props}
    >
      {children}
    </motion.div>
  );
}

/** Fade in rising 8px — the signature entrance for content blocks. */
export function MotionFadeUp({ delay = 0, duration = DURATION.base, children, ...props }: MotionBoxProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 4 }}
      transition={{ duration, delay, ease: EASE }}
      {...props}
    >
      {children}
    </motion.div>
  );
}

/** Fade in growing from 96% — modals, popovers, tooltips. */
export function MotionScale({ delay = 0, duration = DURATION.fast, children, ...props }: MotionBoxProps) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={{ duration, delay, ease: EASE }}
      {...props}
    >
      {children}
    </motion.div>
  );
}

type SlideDirection = 'up' | 'down' | 'left' | 'right';

interface MotionSlideProps extends MotionBoxProps {
  direction?: SlideDirection;
  /** How far it travels in px. */
  distance?: number;
}

/** Fade in sliding from a direction — toasts, drawers, pull-outs. */
export function MotionSlide({ direction = 'up', distance = 16, delay = 0, duration = DURATION.base, children, ...props }: MotionSlideProps) {
  const axis = direction === 'up' || direction === 'down' ? 'y' : 'x';
  const sign = direction === 'up' || direction === 'left' ? -1 : 1;
  const from = { opacity: 0, [axis]: sign * distance } as const;
  const to = { opacity: 1, [axis]: 0 } as const;
  return (
    <motion.div
      initial={from}
      animate={to}
      exit={from}
      transition={{ duration, delay, ease: EASE }}
      {...props}
    >
      {children}
    </motion.div>
  );
}

/** Parent that reveals its <MotionStaggerItem> children one after another. */
export function MotionStagger({ delay = 0, stagger = 0.06, children, ...props }: MotionBoxProps & { stagger?: number }) {
  const transition = { staggerChildren: stagger, delayChildren: delay };
  return (
    <motion.div
      initial="hidden"
      animate="visible"
      exit="hidden"
      variants={{ hidden: { transition }, visible: { transition } }}
      {...props}
    >
      {children}
    </motion.div>
  );
}

/** A single staggered child — must sit inside a <MotionStagger>. */
export function MotionStaggerItem({ duration = DURATION.base, children, ...props }: MotionBoxProps) {
  return (
    <motion.div variants={STAGGER_ITEM} transition={{ duration, ease: EASE }} {...props}>
      {children}
    </motion.div>
  );
}

const STAGGER_ITEM: Variants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0 },
};

/**
 * App-wide motion settings. Mount once, near the root:
 *
 *   <MotionProvider><App /></MotionProvider>
 *
 * `reducedMotion="user"` disables transform animations for users who ask for
 * reduced motion — one switch, every animation in the app honours it.
 */
export function MotionProvider({ children }: { children: ReactNode }) {
  return <MotionConfig reducedMotion="user">{children}</MotionConfig>;
}

const CONFLICTING_DOM_PROPS = [
  'onDrag',
  'onDragStart',
  'onDragEnd',
  'onAnimationStart',
  'onAnimationEnd',
  'onAnimationComplete',
] as const;

/**
 * React's DOM event handlers collide with Framer Motion's gesture props of the
 * same name (a DOM `onDrag`/`onAnimationStart` is not motion's API), so a plain
 * HTML props object cannot be spread straight into a <motion.*> element.
 * Strip the colliding keys before spreading:
 *
 *   <motion.button whileTap={{ scale: 0.97 }} {...stripConflictProps(props)} />
 */
export function stripConflictProps<T extends Record<string, unknown>>(props: T) {
  const {
    onDrag,
    onDragStart,
    onDragEnd,
    onAnimationStart,
    onAnimationEnd,
    onAnimationComplete,
    ...rest
  } = props;
  void onDrag;
  void onDragStart;
  void onDragEnd;
  void onAnimationStart;
  void onAnimationEnd;
  void onAnimationComplete;
  return rest as Omit<T, (typeof CONFLICTING_DOM_PROPS)[number]>;
}